/**
 * @file populationGroupService.js
 * @description APIs for Population Group related requests.
 **/

import { popGroupHttp } from './httpService';

/**
 * Retrieve the tenant ID from local storage.
 * @returns {string|null} The tenant ID or null if not found.
 */
const getTenantId = () => +window.localStorage.getItem('tenantId');

/**
 * Fetches the list of population groups for a tenant.
 * @param {int} tenantId
 * @param {int} onlyEnabled will only return enabled population groups
 * @returns {Promise<Array>} A promise that resolves to the list of population groups.
 */
export const fetchPopulationGroups = async (
    tenantId = null,
    onlyEnabled = true,
) => {
    try {
        if (!tenantId) tenantId = getTenantId();
        const response = await popGroupHttp.get(
            `/v2/population-grouper/population-group/tenant/${tenantId}?onlyEnabled=${onlyEnabled}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching population groups:', error);
        throw error;
    }
};

/**
 * Fetches a population group by ID.
 *
 * @param {number} groupId The ID of the population group.
 * @returns {Promise<Object>} A promise that resolves to the population group data.
 */
export const fetchPopulationGroupByID = async groupId => {
    if (!groupId) return;
    try {
        const response = await popGroupHttp.get(
            `/v2/population-grouper/population-group/${groupId}`,
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error fetching population group with ID ${groupId}:`,
            error,
        );
        throw error;
    }
};

/**
 * Adds a new population group.
 *
 * @param {Object} newGroup The data for the new population group.
 * @returns {Promise<Object>} A promise that resolves to the added population group data.
 */
export const addGroup = async newGroup => {
    try {
        newGroup.tenantId = getTenantId();
        const response = await popGroupHttp.post(
            '/v2/population-grouper/population-group',
            newGroup,
        );
        return response.data;
    } catch (error) {
        console.error('Error adding new population group:', error);
        throw error;
    }
};

/**
 * Updates a population group.
 *
 * @param {number} groupId The ID of the population group to be updated.
 * @param {Object} updatedGroup The updated data for the population group.
 * @returns {Promise<Object>} A promise that resolves to the updated population group data.
 */
export const updateGroup = async updatedGroup => {
    try {
        const payload = {
            ...updatedGroup,
            tenantId: getTenantId(),
        };
        const response = await popGroupHttp.put(
            '/v2/population-grouper/population-group',
            payload,
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error updating population group with ID ${updatedGroup.id}:`,
            error,
        );
        throw error;
    }
};

/**
 * Deletes population groups by IDs.
 *
 * @param {Array<number>} groupIds The IDs of the groups to be deleted.
 * @returns {Promise<Object>} A promise that resolves to the deletion status.
 */
export const deleteGroups = async groupId => {
    try {
        const response = await popGroupHttp.delete(
            `/v2/population-grouper/population-group/${groupId}`,
        );
        return response.data;

        // return true;
    } catch (error) {
        console.error('Error deleting population groups:', error);
        throw error;
    }
};

/**
 * Fetches the list of items for dropdown selection.
 *
 * @returns {Promise<Array>} A promise that resolves to the list of dropdown items.
 */
export const fetchItems = async () => {
    try {
        const tenantId = getTenantId();
        const response = await popGroupHttp.get(
            `/v2/population-grouper/field${tenantId === 0 ? '' : `?tenantId=${tenantId}`}`,
        );
        const sortedData = response.data.sort((a, b) =>
            a.name.localeCompare(b.name),
        );
        return sortedData;
    } catch (error) {
        console.error('Error fetching items for dropdown:', error);
        throw error;
    }
};

/**
 * Fetches the list of operators for dropdown selection.
 *
 * @returns {Promise<Array>} A promise that resolves to the list of dropdown operators.
 */
export const fetchOperators = async () => {
    try {
        const response = await popGroupHttp.get(
            '/v2/population-grouper/operator',
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching operators for dropdown:', error);
        throw error;
    }
};

/**
 * Fetches the "memberOf" values for an individual.
 *
 * @param {number} individualId The ID of the individual.
 * @returns {Promise<Object>} A promise that resolves to the "memberOf" values.
 */
export const fetchMemberOf = async individualId => {
    try {
        const response = await popGroupHttp.get(
            `/v2/population-grouper/individual/${individualId}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching memberOf values:', error);
        throw error;
    }
};

/**
 * Fetches the list of population groups with pagination and additional filters.
 *
 * @param {number} tenantId The ID of the tenant.
 * @param {number} page The page number for pagination.
 * @param {number} pageSize The number of items per page.
 * @param {boolean} includeCustom Whether to include custom population groups.
 * @param {boolean} includeSystem Whether to include system population groups.
 * @param {boolean} onlyEnabled Whether to only include enabled population groups.
 * @returns {Promise<Array>} A promise that resolves to the list of population groups.
 */
export const fetchPopulationGroupsWithFilters = async (
    tenantId,
    page = 1,
    pageSize = 20,
    includeCustom = true,
    includeSystem = true,
    onlyEnabled = false,
) => {
    try {
        if (!tenantId) tenantId = getTenantId();
        const response = await popGroupHttp.get(
            `/v2/population-grouper/population-group/tenant/${tenantId}?page=${page}&pageSize=${pageSize}&includeCustom=${includeCustom}&includeSystem=${includeSystem}&onlyEnabled=${onlyEnabled}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching population groups with filters:', error);
        throw error;
    }
};

/**
 * Searches for population groups with pagination and additional filters.
 *
 * @param {number} tenantId The ID of the tenant.
 * @param {number} page The page number for pagination.
 * @param {number} pageSize The number of items per page.
 * @param {boolean} includeCustom Whether to include custom population groups.
 * @param {boolean} includeSystem Whether to include system population groups.
 * @param {boolean} onlyEnabled Whether to only include enabled population groups.
 * @param {string} search search criteria for population group names
 * @param {string} sort sort order
 * @returns {Promise<Array>} A promise that resolves to the list of population groups.
 */
export const searchPopulationGroups = async (
    tenantId,
    page = 1,
    pageSize = 20,
    includeCustom = true,
    includeSystem = true,
    onlyEnabled = false,
    search = '',
    sort = '',
) => {
    try {
        if (!tenantId) tenantId = getTenantId();
        const response = await popGroupHttp.get(
            `/v2/population-grouper/population-group/tenant/${tenantId}/search?page=${page}&pageSize=${pageSize}&includeCustom=${includeCustom}&includeSystem=${includeSystem}&onlyEnabled=${onlyEnabled}&search=${search}&sort=${sort}`,
        );
        return response.data;
    } catch (error) {
        console.error('Error searching population groups:', error);
        throw error;
    }
};

/**
 * Fetches population group individuals
 *
 * @param {number} groupId The ID of the population group.
 * @param {number} page The page being requested
 * @param {number} pageSize The number of items in the page
 * @param {string} search The name of the individual being searched for
 * @param {string} sort The sort order
 * @returns {Promise<Object>} A promise that resolves to the population group individual data
 */
export const fetchPopulationGroupIndividuals = async (
    populationGroupId,
    page = 1,
    pageSize = 20,
    search = '',
    sort = '',
) => {
    if (!populationGroupId) return;
    try {
        const response = await popGroupHttp.get(
            `/v2/population-grouper/population-group/${populationGroupId}/individuals?tenantId=${getTenantId()}&page=${page}&pageSize=${pageSize}&search=${search}&sort=${sort}`,
        );
        return response.data;
    } catch (error) {
        console.error(
            `Error fetching population group individuals for group Id ${populationGroupId}:`,
            error,
        );
        throw error;
    }
};

/**
 * Fetches every individual within a population group by walking all pages.
 *
 * @param {number} populationGroupId The ID of the population group.
 * @param {string} search The name of the individual being searched for
 * @param {string} sort The sort order
 * @returns {Promise<Array>} A promise that resolves to every individual in the group
 */
export const fetchAllPopulationGroupIndividuals = async (
    populationGroupId,
    search = '',
    sort = '',
) => {
    if (!populationGroupId) return [];

    const EXPORT_PAGE_SIZE = 500;

    const firstPage = await fetchPopulationGroupIndividuals(
        populationGroupId,
        1,
        EXPORT_PAGE_SIZE,
        search,
        sort,
    );
    const firstPageIndividuals = firstPage?.data || [];
    const recordCount = firstPage?.recordCount || firstPageIndividuals.length;
    const totalPages = Math.ceil(recordCount / EXPORT_PAGE_SIZE);
    const remainingPageNumbers = Array.from(
        { length: Math.max(totalPages - 1, 0) },
        (unusedValue, index) => index + 2,
    );

    const remainingPages = await Promise.all(
        remainingPageNumbers.map(pageNumber =>
            fetchPopulationGroupIndividuals(
                populationGroupId,
                pageNumber,
                EXPORT_PAGE_SIZE,
                search,
                sort,
            ),
        ),
    );

    return remainingPages.reduce(
        (allIndividuals, page) => [...allIndividuals, ...(page?.data || [])],
        firstPageIndividuals,
    );
};
